import type { AdminUserDto } from '@bravo/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/DataTable';
import { Badge, Button, Empty, ErrorBlock, PageHeader, Spinner, TextInput, statusTone } from '../components/ui';
import { apiPage } from '../lib/api';
import { useFormat } from '../lib/format';
import { useDebounced } from '../lib/useDebounced';

export function UsersPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const queryText = useDebounced(q, 300);
  const query = useInfiniteQuery({
    queryKey: ['users', queryText],
    queryFn: ({ pageParam }) =>
      apiPage<AdminUserDto>('/admin/users', { q: queryText, limit: 20, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const columns: ColumnDef<AdminUserDto>[] = [
    { id: 'name', header: t('user'), cell: ({ row }) => row.original.displayName || row.original.phone || row.original.id },
    { id: 'phone', header: t('users.phone'), cell: ({ row }) => row.original.phone || '—' },
    { id: 'balance', header: t('users.balance'), cell: ({ row }) => format.etb(row.original.balanceAvailable) },
    {
      id: 'status',
      header: t('status'),
      cell: ({ row }) => (
        <Badge tone={statusTone(row.original.status)}>{format.status(row.original.status)}</Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t('users.title')} subtitle={t('users.subtitle')} />
      <TextInput
        className="mb-4 max-w-md"
        placeholder={t('users.search')}
        value={q}
        onChange={(event) => setQ(event.target.value)}
      />
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} /> : null}
      {!query.isLoading && items.length === 0 ? <Empty title={t('users.empty')} /> : null}
      {items.length > 0 ? (
        <DataTable columns={columns} data={items} onRow={(row) => navigate(`/users/${row.id}`)} />
      ) : null}
      {query.hasNextPage ? (
        <Button className="mt-4" variant="secondary" onClick={() => void query.fetchNextPage()}>
          {t('loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
